import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram } from "lucide-react";
import client from "@/lib/glossyApi";
import { toast } from "sonner";

function SubscribeBox() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const sub = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post("/newsletter/subscribe", { email, origin_url: window.location.origin });
      toast.success(data?.pending ? "One more step — we've emailed you a confirmation link. Click it and you're in." : "You're on the list — the next edition will find you.");
      setEmail("");
    }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not subscribe."); }
    setBusy(false);
  };
  return (
    <form onSubmit={sub} className="mt-8 max-w-md" data-testid="newsletter-subscribe">
      <p className="overline text-gloss-gold mb-3">The fortnightly edit — straight to your inbox</p>
      <div className="flex">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
          className="flex-1 bg-transparent border border-cream/30 border-r-0 px-4 py-3 font-sans text-sm focus:outline-none focus:border-gloss-gold transition-colors placeholder:text-cream/40"
          data-testid="subscribe-email-input" />
        <button type="submit" disabled={busy} className="bg-cream text-ink px-5 overline hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="subscribe-btn">
          {busy ? "…" : "Subscribe"}
        </button>
      </div>
      <p className="mt-3">
        <Link to="/newsletters" className="font-sans text-sm text-cream/50 link-underline hover:text-gloss-gold transition-colors" data-testid="footer-archive-link">Past editions →</Link>
      </p>
    </form>
  );
}

export default function GlossyFooter() {
  return (
    <footer className="bg-ink text-cream relative grain overflow-hidden" data-testid="main-footer">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-b border-cream/15 pb-16">
          <div className="md:col-span-6">
            <h2 className="font-serif text-5xl sm:text-7xl leading-none tracking-tight">S/ME</h2>
            <p className="font-sans text-base text-cream/70 mt-6 max-w-md leading-relaxed">
              The online magazine that makes British business cool. Real small business people, made into editorial stars.
            </p>
            <p className="font-sans text-sm text-cream/50 mt-6 max-w-md leading-relaxed">
              Membership: £30 a month or £300 a year — everything an independent business needs to be seen.{" "}
              <Link to="/join" className="text-gloss-gold link-underline" data-testid="footer-membership-link">Become a member →</Link>
            </p>
            <SubscribeBox />
          </div>
          <div className="md:col-span-3">
            <p className="overline text-cream/50 mb-5">Explore</p>
            <ul className="space-y-3 font-sans">
              <li><Link to="/read" className="link-underline hover:text-gloss-gold">The Edit</Link></li>
              <li><Link to="/directory" className="link-underline hover:text-gloss-gold">Directory</Link></li>
              <li><Link to="/noticeboard" className="link-underline hover:text-gloss-gold">The Noticeboard</Link></li>
              <li><Link to="/members-desk" className="link-underline hover:text-gloss-gold">The Members' Desk</Link></li>
              <li><Link to="/membership" className="link-underline hover:text-gloss-gold">The Membership Year</Link></li>
              <li><Link to="/media-kit" className="link-underline hover:text-gloss-gold">Media Kit</Link></li>
              <li><Link to="/archive" className="link-underline hover:text-gloss-gold">The Archive</Link></li>
              <li><Link to="/privacy" className="link-underline hover:text-gloss-gold">Privacy Policy</Link></li>
              <li><Link to="/cookies" className="link-underline hover:text-gloss-gold">Cookie Policy</Link></li>
              <li><Link to="/terms" className="link-underline hover:text-gloss-gold">Terms of Use</Link></li>
              <li><Link to="/membership-terms" className="link-underline hover:text-gloss-gold">Membership Terms</Link></li>
              <li><Link to="/join" className="link-underline hover:text-gloss-gold">Become a Member</Link></li>
              <li><Link to="/join#demo" className="link-underline text-gloss-gold hover:text-cream" data-testid="footer-demo-link">Watch the 90-second demo</Link></li>
              <li><Link to="/join#book-call" className="link-underline text-gloss-gold hover:text-cream" data-testid="footer-book-call-link">Book a discovery call</Link></li>
              <li><Link to="/join#faq" className="link-underline hover:text-gloss-gold" data-testid="footer-faq-link">Member FAQ</Link></li>
              <li><Link to="/contact" className="link-underline hover:text-gloss-gold" data-testid="footer-contact-link">Contact us</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="overline text-cream/50 mb-5">Follow</p>
            <div className="flex gap-4">
              <a href="https://instagram.com/smemagazineuk" target="_blank" rel="noopener noreferrer" className="w-11 h-11 border border-cream/30 flex items-center justify-center hover:bg-cream hover:text-ink transition-colors" aria-label="Instagram" data-testid="footer-instagram-link"><Instagram size={18} strokeWidth={1.5} /></a>
            </div>
            <p className="font-mono text-xs text-cream/40 mt-6 leading-relaxed">Daily on Instagram.<br/>United Kingdom.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-8 gap-4">
          <p className="font-mono text-xs text-cream/40">© {new Date().getFullYear()} S/ME — The UK's Online SME Magazine</p>
          <Link to="/contact" className="font-mono text-xs text-cream/40 hover:text-gloss-gold transition-colors" data-testid="footer-contact-email">Write to the desk</Link>
        </div>
      </div>
    </footer>
  );
}
